// components/providers/LenisProvider.tsx
"use client";

import Lenis from "lenis";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";

// ----------------------------------------
// Types
// ----------------------------------------

/** Shape of the value exposed via LenisContext */
interface LenisContextType {
  lenis: Lenis | null;
  /**
   * scrollTo — scroll to a target with automatic navbar offset applied.
   * The offset can be overridden per call if needed.
   */
  scrollTo: (
    target: string | number | HTMLElement,
    options?: Parameters<Lenis["scrollTo"]>[1],
  ) => void;
  /** Live navbar height in px — useful for padding/margin calculations */
  navbarHeight: number;
  /** Register the navbar height from useNavbarHeight hook */
  setNavbarHeight: (height: number) => void;
}

// ----------------------------------------
// Context
// ----------------------------------------

/**
 * LenisContext holds the Lenis instance, scrollTo utility,
 * and the live navbar height for offset-aware scrolling.
 */
const LenisContext = createContext<LenisContextType>({
  lenis: null,
  scrollTo: () => {},
  navbarHeight: 0,
  setNavbarHeight: () => {},
});

// ----------------------------------------
// Hook
// ----------------------------------------

/**
 * useLenis — consume the Lenis context in any client component.
 *
 * @example
 * const { scrollTo, navbarHeight } = useLenis()
 * scrollTo('#section') // navbar offset applied automatically
 */
export function useLenis() {
  return useContext(LenisContext);
}

// ----------------------------------------
// Provider
// ----------------------------------------

interface LenisProviderProps {
  children: ReactNode;
}

/**
 * LenisProvider — initializes the Lenis smooth scroll instance once
 * on mount and exposes it to the entire component tree via context.
 *
 * Place this in app/layout.tsx wrapping {children} so it persists
 * across all route navigations without re-mounting.
 */
export default function LenisProvider({ children }: LenisProviderProps) {
  // useRef to hold the Lenis instance so it persists across renders
  // without triggering re-renders when it changes
  const lenisRef = useRef<Lenis | null>(null);

  // Live navbar height — updated by useNavbarHeight via setNavbarHeight
  const [navbarHeight, setNavbarHeight] = useState(0);

  useEffect(() => {
    // Initialize Lenis with scroll configuration
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // exponential ease out
    });

    // Store the instance in the ref so scrollTo can access it
    lenisRef.current = lenis;

    // The RAF loop drives Lenis scroll animation forward on every
    // frame, synced to the display refresh rate
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Cleanup: destroy Lenis on unmount to free memory and listeners
    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  /**
   * scrollTo — programmatically scroll to a target.
   * Automatically applies the current navbar height as a negative offset
   * so the target is never hidden behind the fixed navbar.
   * The offset can be overridden per call if needed.
   *
   * @param target  CSS selector, pixel value, or DOM element
   * @param options Lenis scrollTo options — offset here overrides the default
   */
  const scrollTo: LenisContextType["scrollTo"] = (target, options) => {
    lenisRef.current?.scrollTo(target, {
      // Default offset is negative navbar height so target clears the navbar.
      // If the caller passes their own offset, that takes precedence.
      offset: -navbarHeight,
      ...options,
    });
  };

  return (
    <LenisContext.Provider
      value={{
        lenis: lenisRef.current,
        scrollTo,
        navbarHeight,
        setNavbarHeight,
      }}
    >
      {children}
    </LenisContext.Provider>
  );
}
